import { useEffect, useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import { getMessage } from '../../../utils/messageUtils'
import {
  getStorage,
  type storageProduct,
  type tupper,
} from '../../../utils/storageUtils'

import './Storage.scss'
import StorageProductList from './StorageProductList'
import ListHeader from '../ListHeader'
import TupperList from './TupperList'
import Popup from '../../PopupForm/Popup'
import Spinner from '../../Spinner/Spinner'
import StoragePopupContent from './StoragePopupContent'
import { getProductList, type product } from '../../../utils/productUtils'
import { getRecipeList, type recipe } from '../../../utils/recipeUtils'

interface StorageProps {
  user: User
  isMobile: boolean
}

function Storage({ user, isMobile }: StorageProps) {
  const [storageProducts, setStorageProducts] = useState<
    storageProduct[] | undefined
  >([])
  const [tuppers, setTuppers] = useState<tupper[] | undefined>(undefined)
  const [loadData, setLoadData] = useState<boolean>(true)

  const [allProducts, setAllProducts] = useState<product[] | undefined>(
    undefined
  )
  const [allRecipes, setAllRecipes] = useState<recipe[] | undefined>(undefined)

  // Popup
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<() => void>(() => {})

  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [isLoadingTuppers, setIsLoadingTuppers] = useState(false)
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false)

  useEffect(() => {
    const fetchStorage = async () => {
      setIsLoadingProducts(true)
      setIsLoadingTuppers(true)
      setLoadData(false)
      try {
        const data = await getStorage(user.id!)
        if (data.success) {
          setStorageProducts(data.products)
          setTuppers(data.tuppers)
        }
      } finally {
        setIsLoadingProducts(false)
        setIsLoadingTuppers(false)
      }
    }
    if (loadData) {
      fetchStorage()
    }
  }, [loadData, user.id])

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true)
      try {
        const data = await getProductList(user.id!)
        if (data.success) {
          setAllProducts(data.products)
        }
      } finally {
        setIsLoadingProducts(false)
      }
    }
    const fetchRecipes = async () => {
      setIsLoadingRecipes(true)
      try {
        const data = await getRecipeList(user.id!)
        if (data.success) {
          setAllRecipes(data.recipes)
        }
      } finally {
        setIsLoadingRecipes(false)
      }
    }
    if (allProducts === undefined) {
      fetchProducts()
    }
    if (allRecipes === undefined) {
      fetchRecipes()
    }
  }, [allProducts, allRecipes, user.id])

  function handleNewProduct() {
    openPopup(
      getMessage('product_new'),
      <StoragePopupContent
        user={user}
        product={null}
        isMobile={isMobile}
        allProducts={allProducts}
        existingProducts={storageProducts}
        setOnOk={setPopupOnOk}
        reLoadData={() => setLoadData(true)}
        closePopup={() => setPopupOpen(false)}
        setIsLoading={setIsLoadingProducts}
      />
    )
  }

  function handleNewTupper() {
    openPopup(
      getMessage('tupper_new'),
      <StoragePopupContent
        user={user}
        tupper={null}
        isMobile={isMobile}
        allRecipes={allRecipes}
        existingTuppers={tuppers}
        setOnOk={setPopupOnOk}
        reLoadData={() => setLoadData(true)}
        closePopup={() => setPopupOpen(false)}
        setIsLoading={setIsLoadingTuppers}
      />
    )
  }

  function openPopup(message: string, children: React.ReactNode) {
    setPopupMessage(message)
    setPopupChildren(children)
    setPopupOpen(true)
  }

  const addProductButton = (
    <button className="storage-add-button" onClick={handleNewProduct}>
      {getMessage('add')}
    </button>
  )

  const addTupperButton = (
    <button className="storage-add-button" onClick={handleNewTupper}>
      {getMessage('add')}
    </button>
  )

  const columnClass = `storage-column ${isMobile ? 'storage-column-mobile' : ''}`
  return (
    <div
      className={`storage-container ${isMobile ? 'storage-container-mobile' : ''}`}
    >
      <div className={columnClass}>
        <div className="storage-column-header">
          <ListHeader
            title={getMessage('products')}
            isMobile={isMobile}
            extraChildren={addProductButton}
          />
        </div>
        <StorageProductList
          user={user}
          products={storageProducts}
          isMobile={isMobile}
          reloadData={() => setLoadData(true)}
          openPopup={openPopup}
          closePopup={() => setPopupOpen(false)}
          setPopupOnOk={setPopupOnOk}
          setIsLoading={setIsLoadingProducts}
        />
      </div>
      <div className={columnClass}>
        <ListHeader
          title={getMessage('tuppers')}
          isMobile={isMobile}
          extraChildren={addTupperButton}
        />
        <TupperList
          user={user}
          tuppers={tuppers}
          isMobile={isMobile}
          reloadData={() => setLoadData(true)}
          openPopup={openPopup}
          closePopup={() => setPopupOpen(false)}
          setPopupOnOk={setPopupOnOk}
          setIsLoading={setIsLoadingTuppers}
        />
      </div>
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        isMobile={isMobile}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
      <Spinner
        isOpen={isLoadingTuppers || isLoadingProducts || isLoadingRecipes}
        overlay={true}
      />
    </div>
  )
}

export default Storage
